/**
 * Grading / Repair view — submissions each carry an array of Cards with
 * per-card Service Fee, Pre-FMV, Grade, Post-FMV.
 */
import { DB } from "../lib/db";
import { slicePage, paginationControlsHtml } from "../utils/pagination";
import { icon } from "../utils/icon";
import { downloadCsv } from "../lib/csv";
import { todayISO } from "../utils/format";
import { createRowSelection } from "../components/rowSelection";
import { escapeHtml } from "../lib/dom";
import { fmt$, fmt$0, fmtN, fmtDate, sum, kpiBar } from "../utils/format";
import type { Row } from "../lib/types";

const sel = createRowSelection("grading");
function gradingEdit(id: string): void { (window as any).editGrading?.(id); }
function gradingDelete(id: string): void { (window as any).deleteRow?.("grading", "Submission ID", id); }

interface GradingRowView extends Row {
  _totalFees: number;
  _totalPostFMV: number;
}

function normalizeGrading(s: Row): GradingRowView {
  const cards: any[] = Array.isArray(s.Cards) ? s.Cards : [];
  const v = s as GradingRowView;
  v._totalFees    = cards.reduce((acc, c) => acc + (Number(c["Service Fee"]) || 0), 0);
  v._totalPostFMV = cards.reduce((acc, c) => acc + (Number(c["Post-FMV"])     || 0), 0);
  return v;
}

function summarizeGradingCards(cards: any[] | undefined): string {
  if (!Array.isArray(cards) || !cards.length) return `<span class="text-[var(--muted)]">—</span>`;
  if (cards.length === 1) {
    const c = cards[0];
    const grade = c["Grade"] ? ` <span class="pill pill-violet">${escapeHtml(c["Grade"])}</span>` : "";
    return escapeHtml(c["Card Name"] || c["Card Lot ID"] || "?") + grade;
  }
  const titles = cards.map(c => `${c["Card Name"] || c["Card Lot ID"] || "?"}${c["Grade"] ? ` (${c["Grade"]})` : ""}`).join("\n");
  return `<span title="${escapeHtml(titles)}"><b>${cards.length}</b> cards</span>`;
}

/** Invoice cell: shows the invoice number (and/or a paperclip) as a link that
 *  opens the attached invoice file. stopPropagation so it doesn't trigger the
 *  row's edit handler. */
function invoiceCell(s: Row): string {
  const id = escapeHtml(s["Submission ID"] || "");
  const num = (s["Invoice Number"] || "").toString().trim();
  const hasFile = !!s["Invoice File"];
  if (!num && !hasFile) return `<span class="text-[var(--muted)]">—</span>`;
  const label = num || "View";
  const clip = hasFile ? "📎 " : "";
  if (hasFile) {
    return `<a class="text-cyan-300 hover:underline cursor-pointer" title="View invoice" onclick="event.stopPropagation(); viewInvoiceForGrading('${id}')">${clip}${escapeHtml(label)}</a>`;
  }
  return escapeHtml(num);
}

function statusPill(status: string | undefined): string {
  if (status === "Returned") return "green";
  if (status === "At Service" || status === "At Grading") return "amber";
  return "gray";
}

/** Download every submission as a CSV, one line per card. */
export function exportGradingCsv(): void {
  const lines: any[] = [];
  for (const g of DB.table("grading")) {
    for (const c of (Array.isArray(g.Cards) ? g.Cards : []) as any[]) {
      lines.push({ g, c });
    }
  }
  downloadCsv(`grading-${todayISO()}.csv`, lines, [
    { label: "Submission",     get: x => x.g["Submission ID"] },
    { label: "Date Sent",      get: x => x.g["Date Sent"] },
    { label: "Date Returned",  get: x => x.g["Date Returned"] },
    { label: "Provider",       get: x => x.g["Provider"] },
    { label: "Status",         get: x => x.g["Status"] },
    { label: "Card Lot ID",    get: x => x.c["Card Lot ID"] },
    { label: "Card Name",      get: x => x.c["Card Name"] },
    { label: "Set",            get: x => x.c["Set"] },
    { label: "Service Fee",    get: x => x.c["Service Fee"] },
    { label: "Pre-FMV",        get: x => x.c["Pre-FMV"] },
    { label: "Grade",          get: x => x.c["Grade"] },
    { label: "Post-FMV",       get: x => x.c["Post-FMV"] },
    { label: "Capitalized Into Basis", get: x => x.c["Basis Applied Amount"] },
  ]);
}
(window as any).exportGradingCsv = exportGradingCsv;

export function viewGrading(root: HTMLElement): void {
  const subs = DB.table("grading").slice().reverse().map(normalizeGrading);
  const totalCards   = sum(subs, s => (s.Cards || []).length);
  const totalFees    = sum(subs, s => s._totalFees);
  const totalShip    = sum(subs, s => Number(s["Total Shipping"]) || 0);
  const totalPostFMV = sum(subs, s => s._totalPostFMV);

  const subs_total = subs.length;
  const subs_page = slicePage("grading", subs, 50);
  root.innerHTML = `
    ${kpiBar([
      { label: "Submissions",      value: fmtN(subs.length) },
      { label: "Cards in Service", value: fmtN(totalCards) },
      { label: "Service Fees",     value: fmt$0(totalFees + totalShip) },
      { label: "Post-Grade FMV",   value: fmt$0(totalPostFMV) },
    ])}
    <div class="panel p-3 mb-3 flex flex-wrap gap-2 items-center">
      <button class="btn btn-ghost !py-1 !px-2 text-xs" onclick="exportGradingCsv()" title="Download all submissions as a CSV, one line per card (fees, grades, capitalized amounts)">${icon("download")} CSV</button>
      ${sel.toolbarHtml([
        { id: "edit",   label: `${icon("edit")} Edit`,   title: "Edit selected row", handler: gradingEdit },
        { id: "delete", label: `${icon("trash")} Delete`, title: "Delete selected row", danger: true, handler: gradingDelete },
      ])}
    </div>
    <div class="panel scrollx">
      <table class="t">
        <thead><tr>
          <th>Submission</th><th>Date Sent</th><th>Service</th><th>Provider</th>
          <th>Invoice</th><th>Cards</th><th class="text-right">Service Fees</th><th class="text-right">Shipping</th>
          <th class="text-right">Post-FMV</th><th>Status</th><th>Returned</th>
        </tr></thead>
        <tbody>
          ${subs_page.map(s => {
            const id = escapeHtml(s["Submission ID"] || "");
            const serviceTypes: string[] = Array.isArray(s["Service Types"]) ? s["Service Types"] : [];
            return `<tr ${sel.rowAttrs(id, `editGrading('${id}')`)}>
              <td class="font-mono text-xs"><a class="text-cyan-300 hover:underline cursor-pointer" onclick="showLotHistory('${id}')">${id}</a></td>
              <td>${escapeHtml(fmtDate(s["Date Sent"]))}</td>
              <td class="text-xs">${serviceTypes.map(t => `<span class="pill pill-${t === "Repair" ? "amber" : "violet"}">${escapeHtml(t)}</span>`).join(" ")}</td>
              <td>${escapeHtml(s["Provider"] || "")}</td>
              <td class="text-xs">${invoiceCell(s)}</td>
              <td class="text-xs">${summarizeGradingCards(s.Cards)}</td>
              <td class="text-right stat-num">${fmt$(s._totalFees)}</td>
              <td class="text-right stat-num">${fmt$(s["Total Shipping"])}</td>
              <td class="text-right stat-num">${fmt$(s._totalPostFMV)}</td>
              <td><span class="pill pill-${statusPill(s["Status"])}">${escapeHtml(s["Status"] || "")}</span></td>
              <td>${escapeHtml(fmtDate(s["Date Returned"] || ""))}</td>
            </tr>`;
          }).join("") || `<tr><td colspan="11" class="text-center text-[var(--muted)] py-8">No submissions logged. Click "+ Add Submission".</td></tr>`}
        </tbody>
      </table>
    </div>
    ${paginationControlsHtml("grading", subs_total, 50)}
  `;
  sel.applyUI();
}
